import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Badge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapContainer, TileLayer, Marker, Circle, Polygon, Polyline, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { toast } from "sonner";
import { MapPinAreaIcon, PolygonIcon, CircleIcon, TrashIcon, PencilSimpleIcon, PlusIcon, CheckIcon, XIcon } from "@phosphor-icons/react";
import { fmtDT } from "@/lib/tz";

// Fix Leaflet default icons in CRA
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const numberedIcon = (n) => L.divIcon({
  className: "",
  html: `<div style="display:flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;background:#0ea5e9;color:#fff;font-weight:900;font-size:11px;font-family:monospace;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.5)">${n}</div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

const centerIcon = L.divIcon({
  className: "",
  html: `<div style="width:14px;height:14px;background:#EA580C;border:3px solid #fff;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,.5)"></div>`,
  iconSize: [20, 20], iconAnchor: [10, 10],
});

/** Captures map clicks in draw mode and forwards to parent */
function DrawCaptureLayer({ mode, onCenter, onVertex }) {
  useMapEvents({
    click(e) {
      if (mode === "circle") onCenter({ lat: e.latlng.lat, lng: e.latlng.lng });
      else if (mode === "polygon") onVertex({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

function fmtMeters(m) {
  if (!m || m <= 0) return "—";
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(2)} km`;
}

const emptyForm = {
  id: null, name: "", kind: "circle",
  center_lat: null, center_lng: null, radius_m: 300,
  polygon_points: [], color: "#EA580C", active: true, notes: "",
};

// Free tile providers — no API key needed
const TILE_LAYERS = {
  street: {
    url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
    maxZoom: 19,
  },
};

function MapStyleToggle({ style, onChange }) {
  return (
    <div className="inline-flex border-2 border-slate-300 rounded-sm overflow-hidden bg-white" data-testid="map-style-toggle">
      {["street", "satellite"].map((k) => (
        <button
          key={k}
          type="button"
          data-testid={`map-style-${k}`}
          onClick={() => onChange(k)}
          className={`px-3 py-1 text-xs font-bold uppercase tracking-wider ${style === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
        >{k}</button>
      ))}
    </div>
  );
}

export default function Geofences() {
  const [fences, setFences] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [drawMode, setDrawMode] = useState(null); // "circle" | "polygon" | null
  const [saving, setSaving] = useState(false);
  const [mapStyle, setMapStyle] = useState("street"); // shared street/satellite toggle
  const [addrQuery, setAddrQuery] = useState("");
  const [addrLoading, setAddrLoading] = useState(false);
  const [addrResults, setAddrResults] = useState([]);
  const [drawMapRef, setDrawMapRef] = useState(null);

  const loadFences = async () => {
    try { const { data } = await api.get("/geofences"); setFences(Array.isArray(data) ? data : []); }
    catch { setFences([]); }
  };
  const loadEvents = async () => {
    try { const { data } = await api.get("/geofence-events?limit=50"); setEvents(Array.isArray(data) ? data : []); }
    catch { setEvents([]); }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      await Promise.all([loadFences(), loadEvents()]);
      if (mounted) setLoading(false);
    })();
    const int = setInterval(loadEvents, 20000);
    const onEvt = (e) => {
      if (e?.detail?.entity === "geofence_events") loadEvents();
      if (e?.detail?.entity === "geofences") loadFences();
    };
    window.addEventListener("rf-data-change", onEvt);
    return () => { mounted = false; clearInterval(int); window.removeEventListener("rf-data-change", onEvt); };
  }, []);

  const openNew = (kind) => {
    setForm({ ...emptyForm, kind });
    setDrawMode(kind);
    setAddrQuery(""); setAddrResults([]);
    setEditOpen(true);
  };
  const openEdit = (g) => {
    setForm({
      id: g.id, name: g.name || "", kind: g.kind || "circle",
      center_lat: g.center_lat, center_lng: g.center_lng, radius_m: g.radius_m || 300,
      polygon_points: g.polygon_points || [], color: g.color || "#EA580C",
      active: g.active !== false, notes: g.notes || "",
    });
    setDrawMode(null);
    setAddrQuery(""); setAddrResults([]);
    setEditOpen(true);
  };

  const findAddress = async () => {
    const q = addrQuery.trim();
    if (q.length < 3) { toast.error("Enter at least 3 characters"); return; }
    setAddrLoading(true); setAddrResults([]);
    try {
      const { data } = await api.get(`/geofences/geocode`, { params: { q } });
      const list = Array.isArray(data) ? data : [];
      if (list.length === 0) { toast.error("No matches — try a fuller address"); return; }
      setAddrResults(list);
      // Auto-apply the first hit if there's only one result
      if (list.length === 1) applyAddressPick(list[0]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Geocoding failed");
    } finally { setAddrLoading(false); }
  };

  const applyAddressPick = (pick) => {
    if (!pick) return;
    if (form.kind === "circle") {
      setForm((f) => ({ ...f, center_lat: pick.lat, center_lng: pick.lng, name: f.name || (pick.display_name || "").split(",")[0] }));
    } else {
      // polygon: add as a vertex
      setForm((f) => ({ ...f, polygon_points: [...(f.polygon_points || []), { lat: pick.lat, lng: pick.lng }] }));
    }
    // Center the drawing map on the picked location
    if (drawMapRef) {
      try { drawMapRef.setView([pick.lat, pick.lng], 15); } catch { /* ignore */ }
    }
    setAddrResults([]);
    toast.success("Location placed on map");
  };

  const saveFence = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (form.kind === "circle") {
      if (form.center_lat == null || form.center_lng == null) { toast.error("Click on the map to place the center"); return; }
      if (!form.radius_m || form.radius_m <= 0) { toast.error("Radius must be > 0 meters"); return; }
    } else {
      if (!form.polygon_points || form.polygon_points.length < 3) { toast.error("Polygon needs at least 3 vertices"); return; }
    }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(), kind: form.kind,
        center_lat: form.kind === "circle" ? form.center_lat : null,
        center_lng: form.kind === "circle" ? form.center_lng : null,
        radius_m: form.kind === "circle" ? Number(form.radius_m) : null,
        polygon_points: form.kind === "polygon" ? form.polygon_points : [],
        color: form.color || "#EA580C", active: form.active, notes: form.notes || "",
      };
      if (form.id) await api.put(`/geofences/${form.id}`, body);
      else await api.post("/geofences", body);
      toast.success(form.id ? "Geofence updated" : "Geofence created");
      setEditOpen(false); setDrawMode(null);
      await loadFences();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const deleteFence = async (g) => {
    if (!window.confirm(`Delete geofence "${g.name}"?`)) return;
    try { await api.delete(`/geofences/${g.id}`); toast.success("Deleted"); await loadFences(); }
    catch { toast.error("Delete failed"); }
  };

  const overviewCenter = useMemo(() => {
    if (fences.length && fences[0].kind === "circle" && fences[0].center_lat)
      return [fences[0].center_lat, fences[0].center_lng];
    if (fences.length && fences[0].kind === "polygon" && fences[0].polygon_points?.length)
      return [fences[0].polygon_points[0].lat, fences[0].polygon_points[0].lng];
    return [43.65, -79.38]; // Toronto default
  }, [fences]);

  return (
    <div className="p-8 space-y-6" data-testid="geofences-page">
      <PageHeader
        eyebrow="/// Dispatch"
        title="Geofences"
        subtitle="Draw virtual yards on the map. Get an instant alert when any driver enters or leaves — no more 'did he arrive yet' calls."
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button data-testid="new-circle-btn" onClick={() => openNew("circle")} className="gap-2">
          <CircleIcon size={16} weight="bold" /> Draw circular yard
        </Button>
        <Button data-testid="new-polygon-btn" variant="outline" onClick={() => openNew("polygon")} className="gap-2">
          <PolygonIcon size={16} weight="bold" /> Draw polygon yard
        </Button>
        <div className="ml-auto"><MapStyleToggle style={mapStyle} onChange={setMapStyle} /></div>
      </div>

      {/* Overview map with all geofences (hidden while draw dialog is open to
          prevent the modal map from stacking on top of it visually) */}
      {!editOpen && (
      <div className="border-2 border-slate-200 rounded-sm overflow-hidden" style={{ height: 380 }} data-testid="geofences-overview-map">
        <MapContainer key={`ov-${mapStyle}`} center={overviewCenter} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer url={TILE_LAYERS[mapStyle].url} attribution={TILE_LAYERS[mapStyle].attribution} maxZoom={TILE_LAYERS[mapStyle].maxZoom} />
          {fences.map((g) => {
            if (g.kind === "circle" && g.center_lat != null && g.center_lng != null) {
              return (
                <Circle key={g.id} center={[g.center_lat, g.center_lng]} radius={g.radius_m}
                  pathOptions={{ color: g.color || "#EA580C", weight: 2, fillOpacity: 0.15 }}>
                  <Popup>
                    <b>{g.name}</b><br />
                    Circle · {fmtMeters(g.radius_m)}<br />
                    {g.active ? <span style={{ color: "#059669" }}>Active</span> : <span style={{ color: "#6b7280" }}>Inactive</span>}
                  </Popup>
                </Circle>
              );
            }
            if (g.kind === "polygon" && g.polygon_points?.length >= 3) {
              return (
                <Polygon key={g.id} positions={g.polygon_points.map((p) => [p.lat, p.lng])}
                  pathOptions={{ color: g.color || "#EA580C", weight: 2, fillOpacity: 0.15 }}>
                  <Popup>
                    <b>{g.name}</b><br />
                    Polygon · {g.polygon_points.length} vertices<br />
                    {g.active ? <span style={{ color: "#059669" }}>Active</span> : <span style={{ color: "#6b7280" }}>Inactive</span>}
                  </Popup>
                </Polygon>
              );
            }
            return null;
          })}
        </MapContainer>
      </div>
      )}

      {/* Geofences list */}
      <div>
        <h3 className="text-sm font-black uppercase tracking-widest text-slate-700 mb-3">Yards ({fences.length})</h3>
        {loading ? (
          <div className="text-sm text-slate-500 font-mono">Loading…</div>
        ) : fences.length === 0 ? (
          <div className="text-sm text-slate-500 font-mono border border-dashed border-slate-300 rounded-sm p-6 text-center">
            No yards yet. Click "Draw circular yard" above and tap the map to place one.
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {fences.map((g) => (
              <div key={g.id} className="border-2 border-slate-200 rounded-sm p-4 hover:border-slate-400 transition-colors" data-testid={`geofence-card-${g.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <div className="font-black text-slate-900 text-sm">{g.name}</div>
                    <div className="text-xs text-slate-500 font-mono uppercase tracking-wider">
                      {g.kind === "circle" ? `Circle · ${fmtMeters(g.radius_m)}` : `Polygon · ${g.polygon_points?.length || 0} pts`}
                    </div>
                  </div>
                  <Badge tone={g.active ? "success" : "muted"}>{g.active ? "active" : "off"}</Badge>
                </div>
                {g.notes && <div className="text-xs text-slate-600 mb-3 whitespace-pre-line">{g.notes}</div>}
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(g)} className="gap-1"><PencilSimpleIcon size={12} /> Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteFence(g)} className="text-red-600 gap-1"><TrashIcon size={12} /> Delete</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent events log */}
      <div>
        <h3 className="text-sm font-black uppercase tracking-widest text-slate-700 mb-3">Recent enter/exit events</h3>
        {events.length === 0 ? (
          <div className="text-xs text-slate-500 font-mono border border-dashed border-slate-300 rounded-sm p-4 text-center">
            No events yet — they appear here as drivers cross your geofences.
          </div>
        ) : (
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600 uppercase text-xs tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Driver</th>
                  <th className="text-left px-3 py-2">Yard</th>
                  <th className="text-left px-3 py-2">Event</th>
                  <th className="text-left px-3 py-2">Trip</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100" data-testid={`event-row-${e.id}`}>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{fmtDT(e.at)}</td>
                    <td className="px-3 py-2 font-semibold">{e.driver_name || "—"}</td>
                    <td className="px-3 py-2">{e.geofence_name}</td>
                    <td className="px-3 py-2"><Badge tone={e.kind === "enter" ? "success" : "warning"}>{e.kind}</Badge></td>
                    <td className="px-3 py-2 font-mono text-xs">{e.trip_number || (e.ref_type === "city_shift" ? "city shift" : "—")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Draw / Edit dialog */}
      <Dialog open={editOpen} onOpenChange={(o) => { if (!o) setDrawMode(null); setEditOpen(o); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle data-testid="edit-dialog-title">{form.id ? "Edit yard" : "New yard"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Name *</Label>
                <Input data-testid="fence-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Main terminal" />
              </div>
              <div>
                <Label>Kind</Label>
                <Select value={form.kind} onValueChange={(v) => { setForm({ ...form, kind: v, polygon_points: [] }); setDrawMode(v); }}>
                  <SelectTrigger data-testid="fence-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="circle">Circle</SelectItem>
                    <SelectItem value="polygon">Polygon</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.kind === "circle" && (
                <div className="col-span-2">
                  <Label>Radius (meters)</Label>
                  <Input data-testid="fence-radius-input" type="number" min={10} step={10} value={form.radius_m}
                    onChange={(e) => setForm({ ...form, radius_m: Number(e.target.value) })} />
                </div>
              )}
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything the team should know" />
              </div>
            </div>

            {/* Address search */}
            <div className="border-2 border-slate-100 bg-slate-50 rounded-sm p-3 space-y-2">
              <Label className="text-xs uppercase tracking-widest text-slate-600">Or find by address</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="fence-address-input"
                  placeholder="e.g. 1600 Pennsylvania Ave, Washington DC"
                  value={addrQuery}
                  onChange={(e) => setAddrQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); findAddress(); } }}
                />
                <Button type="button" variant="outline" onClick={findAddress} disabled={addrLoading} data-testid="fence-address-find-btn">
                  {addrLoading ? "Searching…" : "Find"}
                </Button>
              </div>
              {addrResults.length > 1 && (
                <div className="space-y-1 max-h-32 overflow-y-auto" data-testid="fence-address-results">
                  {addrResults.map((r, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => applyAddressPick(r)}
                      className="w-full text-left text-xs px-2 py-1.5 bg-white border border-slate-200 hover:border-slate-500 rounded-sm"
                      data-testid={`fence-address-pick-${i}`}
                    >
                      <div className="truncate">{r.display_name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{r.lat.toFixed(4)}, {r.lng.toFixed(4)}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Drawing map — fresh MapContainer each time the dialog opens
                so Leaflet fully re-initializes and never stacks on top of
                an old canvas. */}
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-widest text-slate-600">
                {form.kind === "circle" ? "Click the map to place the yard center" : "Click the map to add polygon vertices"}
              </Label>
              <MapStyleToggle style={mapStyle} onChange={setMapStyle} />
            </div>
            <div className="border-2 border-slate-200 rounded-sm overflow-hidden bg-slate-50" style={{ height: 340 }} data-testid="draw-map">
              <MapContainer
                key={`draw-${editOpen ? "open" : "closed"}-${form.id || "new"}-${form.kind}-${mapStyle}`}
                center={
                form.center_lat != null ? [form.center_lat, form.center_lng] :
                (form.polygon_points?.[0] ? [form.polygon_points[0].lat, form.polygon_points[0].lng] : [43.65, -79.38])
              } zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom
                whenReady={(e) => { setDrawMapRef(e.target); setTimeout(() => { try { e.target.invalidateSize(); } catch {} }, 250); }}
              >
                <TileLayer url={TILE_LAYERS[mapStyle].url} attribution={TILE_LAYERS[mapStyle].attribution} maxZoom={TILE_LAYERS[mapStyle].maxZoom} />
                {drawMode && (
                  <DrawCaptureLayer mode={form.kind}
                    onCenter={(pt) => setForm((f) => ({ ...f, center_lat: pt.lat, center_lng: pt.lng }))}
                    onVertex={(pt) => setForm((f) => ({ ...f, polygon_points: [...(f.polygon_points || []), pt] }))}
                  />
                )}
                {form.kind === "circle" && form.center_lat != null && (
                  <>
                    <Marker position={[form.center_lat, form.center_lng]} icon={centerIcon} />
                    <Circle center={[form.center_lat, form.center_lng]} radius={Number(form.radius_m) || 100} pathOptions={{ color: form.color, weight: 2, fillOpacity: 0.15 }} />
                  </>
                )}
                {form.kind === "polygon" && (
                  <>
                    {form.polygon_points.map((p, i) => (
                      <Marker key={i} position={[p.lat, p.lng]} icon={numberedIcon(i + 1)} />
                    ))}
                    {form.polygon_points.length >= 2 && (
                      <Polyline positions={form.polygon_points.map((p) => [p.lat, p.lng])} pathOptions={{ color: form.color, weight: 2, dashArray: "6 6", opacity: 0.8 }} />
                    )}
                    {form.polygon_points.length >= 3 && (
                      <Polygon positions={form.polygon_points.map((p) => [p.lat, p.lng])} pathOptions={{ color: form.color, weight: 2, fillOpacity: 0.15 }} />
                    )}
                  </>
                )}
              </MapContainer>
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-600 font-mono">
              {form.kind === "circle" ? (
                form.center_lat != null
                  ? <span className="text-green-700 font-semibold"><CheckIcon size={12} className="inline" /> Center placed at {form.center_lat.toFixed(4)}, {form.center_lng.toFixed(4)}. Adjust radius above.</span>
                  : <span>Click anywhere on the map to place the yard's center.</span>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <span>{form.polygon_points.length < 3
                    ? `Click the map to add vertices (${form.polygon_points.length} placed — need ≥3).`
                    : `${form.polygon_points.length} vertices — click to add more.`}
                  </span>
                  {form.polygon_points.length > 0 && (
                    <Button size="sm" variant="ghost" data-testid="undo-vertex-btn"
                      onClick={() => setForm((f) => ({ ...f, polygon_points: f.polygon_points.slice(0, -1) }))}
                      className="text-slate-600 gap-1"><XIcon size={12} /> Undo last</Button>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEditOpen(false); setDrawMode(null); }}>Cancel</Button>
            <Button data-testid="save-fence-btn" onClick={saveFence} disabled={saving}>{saving ? "Saving…" : (form.id ? "Save changes" : "Create yard")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
